import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-alt font-medium transition-[color,box-shadow] cursor-pointer disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-all",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-black shadow-xs hover:bg-primary/70",
        defaultLight:
          "bg-primary-50 border border-primary-700 text-primary-700 shadow-xs hover:bg-primary-400/70",
        optional:
          "bg-optional-700 text-white shadow-xs hover:bg-optional-900",
        destructive:
          "bg-red-300 text-black shadow-xs hover:bg-red-400 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        outline:
          "border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground",
        outlineSolid:
          "border border-dark-grey bg-background shadow-xs hover:bg-accent hover:text-accent-foreground",
        popinLogin:
          "border border-dark-grey bg-background shadow-xs hover:bg-accent hover:text-accent-foreground justify-start gap-4",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        white: 
          "bg-white text-black shadow-xs hover:bg-white/90",
        dark: 
          "bg-dark text-white shadow-xs hover:bg-dark/90",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        linkGray: "text-gray-600 underline-offset-4 underline",
      },
      size: {
        default: "h-9 px-5 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-full px-6 has-[>svg]:px-4",
        xl: "h-12 rounded-full px-6 has-[>svg]:px-4 font-semibold text-md",
        popinLogin: "h-12 rounded-full px-10 has-[>svg]:px-4 font-semibold text-md",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
